import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import { Search, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getUsers } from '../../Services/Users/UserApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

const selectMenuProps = {
    PaperProps: {
        sx: {
            boxShadow: 'none',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            mt: 0.5,
            '& .MuiMenuItem-root': {
                fontSize: '0.82rem',
                fontWeight: 500,
                py: 0.8,
                '&.Mui-selected': {
                    bgcolor: '#FFFDF9',
                    color: '#D97706',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: '#FFF8EC'
                    }
                }
            }
        }
    }
};

const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(10);

    // Filter states
    const [roleFilter, setRoleFilter] = useState('');
    const [providerFilter, setProviderFilter] = useState('');
    const [verifiedFilter, setVerifiedFilter] = useState('');
    const [joinedFilter, setJoinedFilter] = useState('');

    // Debounce search input (500ms)
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Active filters object sent directly to backend API
    const activeFilters = useMemo(() => {
        const filters = {};
        if (debouncedSearch.trim()) filters.search = debouncedSearch.trim();
        if (roleFilter) filters.role = roleFilter;
        if (providerFilter) filters.provider = providerFilter;
        if (verifiedFilter === 'verified') filters.isEmailVerified = 'true';
        if (verifiedFilter === 'unverified') filters.isEmailVerified = 'false';
        if (joinedFilter) {
            if (joinedFilter === 'newest' || joinedFilter === 'oldest') {
                filters.sort = joinedFilter;
            } else {
                filters.joined = joinedFilter;
            }
        }
        return filters;
    }, [debouncedSearch, roleFilter, providerFilter, verifiedFilter, joinedFilter]);

    const fetchUsers = useCallback(async (currentPage = 1, filtersObj = {}) => {
        try {
            setLoading(true);
            const actualLimit = limit !== 10 ? limit : undefined;
            const response = await getUsers(currentPage, actualLimit, filtersObj);

            if (response && response.success && response.data) {
                setUsers(response.data);
                setTotal(response.total || response.totalUsers || response.data.length || 0);
                setTotalPages(response.totalPages || 1);
                setError(null);
            } else {
                setUsers([]);
                setError("Failed to load users data.");
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch users. Please try again later.");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Fetch from backend whenever page or filters change
    useEffect(() => {
        fetchUsers(page, activeFilters);
    }, [page, activeFilters, fetchUsers]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
    };

    const handleRoleChange = (e) => {
        setRoleFilter(e.target.value);
        setPage(1);
    };

    const handleProviderChange = (e) => {
        setProviderFilter(e.target.value);
        setPage(1);
    };

    const handleVerifiedChange = (e) => {
        setVerifiedFilter(e.target.value);
        setPage(1);
    };

    const handleJoinedChange = (e) => {
        setJoinedFilter(e.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDebouncedSearch('');
        setRoleFilter('');
        setProviderFilter('');
        setVerifiedFilter('');
        setJoinedFilter('');
        setPage(1);
    };

    const handleViewDetails = (user) => {
        navigate(`/user-details/${user.id}`, { state: { userId: user.id } });
    };

    const renderRoleBadges = (user) => {
        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                <Box
                    sx={{
                        px: 0.85,
                        py: 0.25,
                        borderRadius: '4px',
                        bgcolor: '#F1F5F9',
                        color: '#475569',
                        border: '1px solid #E2E8F0',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                    }}
                >
                    Guest
                </Box>
                {user.isHost && (
                    <Box
                        sx={{
                            px: 0.85,
                            py: 0.25,
                            borderRadius: '4px',
                            bgcolor: '#FEF3C7',
                            color: '#92400E',
                            border: '1px solid #FDE68A',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Host
                    </Box>
                )}
                {user.isAffiliate && (
                    <Box
                        sx={{
                            px: 0.85,
                            py: 0.25,
                            borderRadius: '4px',
                            bgcolor: '#E8F5E8',
                            color: '#2E7D32',
                            border: '1px solid #C8E6C9',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Affiliate
                    </Box>
                )}
            </Stack>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
            >
                <Typography
                    sx={{
                        color: '#222',
                        fontWeight: 500,
                        fontSize: { xs: '1.75rem', md: '1.8rem' },
                    }}
                >
                    Users Management
                </Typography>
            </Stack>

            {/* Search and Filters Bar */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: '8px',
                    border: '1px solid #E8E8E8',
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none'
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1.25 }}>
                    <TextField
                        placeholder="Search by name, email or phone..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                                    <Search size={16} color="#94A3B8" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: { xs: '1 1 100%', sm: '1 1 240px', md: '0 1 280px' },
                            bgcolor: '#FFFFFF',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            },
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 110 } }}>
                        <Select
                            displayEmpty
                            value={roleFilter}
                            onChange={handleRoleChange}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => {
                                if (!selected) return <span style={{ color: '#64748B' }}>Role</span>;
                                const map = { guest: 'Guest', host: 'Host', affiliate: 'Affiliate' };
                                return map[selected] || selected;
                            }}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All Roles</MenuItem>
                            <MenuItem value="guest" sx={{ fontSize: '0.82rem' }}>Guest</MenuItem>
                            <MenuItem value="host" sx={{ fontSize: '0.82rem' }}>Host</MenuItem>
                            <MenuItem value="affiliate" sx={{ fontSize: '0.82rem' }}>Affiliate</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 115 } }}>
                        <Select
                            displayEmpty
                            value={providerFilter}
                            onChange={handleProviderChange}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => {
                                if (!selected) return <span style={{ color: '#64748B' }}>Provider</span>;
                                return selected.charAt(0).toUpperCase() + selected.slice(1);
                            }}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All Providers</MenuItem>
                            <MenuItem value="local" sx={{ fontSize: '0.82rem' }}>Local</MenuItem>
                            <MenuItem value="google" sx={{ fontSize: '0.82rem' }}>Google</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 110 } }}>
                        <Select
                            displayEmpty
                            value={verifiedFilter}
                            onChange={handleVerifiedChange}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => {
                                if (!selected) return <span style={{ color: '#64748B' }}>Verified</span>;
                                return selected === 'verified' ? 'Verified' : 'Unverified';
                            }}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All Status</MenuItem>
                            <MenuItem value="verified" sx={{ fontSize: '0.82rem' }}>Verified</MenuItem>
                            <MenuItem value="unverified" sx={{ fontSize: '0.82rem' }}>Unverified</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 110 } }}>
                        <Select
                            displayEmpty
                            value={joinedFilter}
                            onChange={handleJoinedChange}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => {
                                if (!selected) return <span style={{ color: '#64748B' }}>Joined</span>;
                                const map = {
                                    today: 'Today',
                                    this_week: 'This Week',
                                    this_month: 'This Month',
                                    newest: 'Newest',
                                    oldest: 'Oldest'
                                };
                                return map[selected] || selected;
                            }}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.82rem' }}>All Time</MenuItem>
                            <MenuItem value="today" sx={{ fontSize: '0.82rem' }}>Today</MenuItem>
                            <MenuItem value="this_week" sx={{ fontSize: '0.82rem' }}>This Week</MenuItem>
                            <MenuItem value="this_month" sx={{ fontSize: '0.82rem' }}>This Month</MenuItem>
                            <MenuItem value="newest" sx={{ fontSize: '0.82rem' }}>Newest First</MenuItem>
                            <MenuItem value="oldest" sx={{ fontSize: '0.82rem' }}>Oldest First</MenuItem>
                        </Select>
                    </FormControl>

                    {(searchTerm || roleFilter || providerFilter || verifiedFilter || joinedFilter) && (
                        <Button
                            size="small"
                            onClick={handleClearFilters}
                            startIcon={<RotateCcw size={14} />}
                            sx={{
                                height: '38px',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#803927',
                                borderColor: '#E2E8F0',
                                border: '1px solid #E2E8F0',
                                borderRadius: '6px',
                                px: 1.5,
                                bgcolor: '#FFFFFF',
                                '&:hover': {
                                    bgcolor: '#FFF7ED',
                                    borderColor: '#FDBA74'
                                }
                            }}
                        >
                            Reset
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Table */}
            <Paper
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    overflow: "hidden",
                    boxShadow: 'none'
                }}
                elevation={0}
            >
                <TableContainer>
                    <Table sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '4%' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '4%' }}>Profile</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '13%' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '15%' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '10%' }}>Phone</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '8%' }}>Country</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '7%' }}>Provider</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '12%' }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '6%' }}>Verified</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '6%' }}>Agreed</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '9%' }}>Joined Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', width: '6%' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : users.length > 0 ? (
                                users.map((user, idx) => (
                                    <TableRow
                                        key={user.id}
                                        sx={{
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell sx={{ textAlign: 'center', py: 1.25, px: 1, fontSize: '0.75rem', color: '#64748B' }}>
                                            {(page - 1) * limit + idx + 1}
                                        </TableCell>

                                        <TableCell sx={{ py: 1, px: 1, textAlign: 'center' }}>
                                            <Avatar
                                                src={user.profileImage}
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    border: '1px solid #EEE',
                                                    fontSize: '0.8rem',
                                                    margin: '0 auto',
                                                    bgcolor: '#F8FAFC',
                                                    color: '#1E293B'
                                                }}
                                            >
                                                {user.name?.charAt(0)?.toUpperCase()}
                                            </Avatar>
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 140 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    color: '#1E293B',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title={user.name}
                                            >
                                                {user.name || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 160 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: '#475569',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title={user.email}
                                            >
                                                {user.email || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                            {user.phone || 'N/A'}
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                            {user.country || '-'}
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                            <Box
                                                sx={{
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: '4px',
                                                    bgcolor: user.provider === 'local' ? '#E3F2FD' : '#F3E5F5',
                                                    color: user.provider === 'local' ? '#1976D2' : '#7B1FA2',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {user.provider || 'N/A'}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25 }}>
                                            {renderRoleBadges(user)}
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                            <Box
                                                sx={{
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: '4px',
                                                    bgcolor: user.isEmailVerified ? '#E8F5E8' : '#FFEBEE',
                                                    color: user.isEmailVerified ? '#2E7D32' : '#C62828',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {user.isEmailVerified ? 'Yes' : 'No'}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                            <Box
                                                sx={{
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: '4px',
                                                    bgcolor: user.isAgreed ? '#E8F5E8' : '#FFEBEE',
                                                    color: user.isAgreed ? '#2E7D32' : '#C62828',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {user.isAgreed ? 'Yes' : 'No'}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(user.createdAt)}
                                        </TableCell>

                                        <TableCell align="right" sx={{ py: 1.25, px: 1, whiteSpace: 'nowrap' }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disableElevation
                                                disableFocusRipple
                                                disableRipple
                                                disableTouchRipple
                                                onClick={() => handleViewDetails(user)}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    color: '#334155',
                                                    borderColor: '#CBD5E1',
                                                    borderRadius: '4px',
                                                    height: '28px',
                                                    py: 0,
                                                    px: 1.5,
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        bgcolor: '#F8FAFC',
                                                        borderColor: '#94A3B8',
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                        {searchTerm || roleFilter || providerFilter || verifiedFilter || joinedFilter
                                            ? 'No users found matching your filters.'
                                            : 'No users found.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={total}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newSize) => {
                    setLimit(newSize);
                    setPage(1);
                }}
                loading={loading}
            />
        </Box>
    );
};

export default Users;
